/**
 * Scalping Trend Dashboard Overlay — V2 Compact Top-Left Design
 *
 * Replaces the old wide bottom-center horizontal panel that was hidden
 * in multi-pane / multi-chart layouts.
 *
 * Layout (top-left of chart, compact card ~260px):
 *   - Header strip: Symbol + Live Price, side-tinted (green/red/slate)
 *   - SIGNAL pill (large, glowing) — BUY (green) / SELL (red) / IDLE
 *   - Status pill (Active / TGT-1 hit / TGT-2 hit / SL hit / TSL hit)
 *   - 5-row grid: Entry, TGT-1, TGT-2, SL, Trailing SL with hit ✓ / ✗
 *   - Footer: Live P&L  /  Booked P&L
 *   - Collapse toggle (▲/▼)
 *
 * BUY and SELL signals get distinct colour treatments (gradients, glow,
 * accent bars) while sharing the same structural layout.
 *
 * Arrow badges (Buy/Sell labels at signal candles) are preserved.
 *
 * CSS prefix: st-
 */
import React from "react";
import { ScalpingTrendDashboard } from "../../indicators/built-in/scalping-trend";
import "./ScalpingTrendOverlay.scss";

interface ScalpingTrendOverlayProps {
  dashboard: ScalpingTrendDashboard | null;
  symbol: string;
  visible: boolean;
  showDashboard?: boolean;
  onToggleDashboard?: () => void;
  arrowBadges?: { x: number; y: number; direction: "long" | "short" }[];
}

const ScalpingTrendOverlay: React.FC<ScalpingTrendOverlayProps> = ({
  dashboard,
  symbol,
  visible,
  showDashboard = true,
  onToggleDashboard,
  arrowBadges = [],
}) => {
  if (!visible || !dashboard) return null;

  const {
    signal,
    trend,
    entry,
    stopLoss,
    trailingStop,
    target1,
    target2,
    currentPrice,
    livePL,
    bookedPL,
    status,
    tp1Hit,
    tp2Hit,
    slHit,
    tslHit,
  } = dashboard;

  const isLong = trend === "LONG";
  const isShort = trend === "SHORT";
  const sideClass = isLong ? "st-side-buy" : isShort ? "st-side-sell" : "st-side-idle";
  const signalLabel = signal ?? "IDLE";

  const fmt = (v: number | null) => (v !== null ? v.toFixed(0) : "—");
  const fmtPL = (v: number) => `${v >= 0 ? "+" : ""}${v.toFixed(0)}`;

  // ── Status text + class ────────────────────────────────────────────
  const statusText =
    status === "Target1 Hit" ? "TGT-1 ✓"
    : status === "Target2 Hit" ? "TGT-2 ✓"
    : status === "Stop Loss Hit" ? "SL ✗"
    : status === "Trailing SL Hit" ? "TSL ✗"
    : "Active";

  const statusClass =
    status === "Target1 Hit" || status === "Target2 Hit" ? "st-status--win"
    : status === "Stop Loss Hit" || status === "Trailing SL Hit" ? "st-status--loss"
    : signal ? "st-status--active"
    : "st-status--idle";

  // ── Grid rows — each row is one level ──────────────────────────────
  const rows: {
    label: string;
    value: number | null;
    hit: boolean;
    hitMark: string;
    rowClass: string;
  }[] = [
    { label: "Entry",       value: entry,        hit: !!signal,   hitMark: "●", rowClass: "st-row--entry" },
    { label: "TGT - 1",     value: target1,      hit: tp1Hit,     hitMark: "✓", rowClass: "st-row--tgt" },
    { label: "TGT - 2",     value: target2,      hit: tp2Hit,     hitMark: "✓", rowClass: "st-row--tgt" },
    { label: "SL",          value: stopLoss,     hit: slHit,      hitMark: "✗", rowClass: "st-row--sl" },
    { label: "Trailing SL", value: trailingStop, hit: tslHit,     hitMark: "✗", rowClass: "st-row--sl" },
  ];

  return (
    <>
      {/* ─── Arrow Badges at signal bars ─── */}
      {arrowBadges.map((badge, i) => (
        <div
          key={i}
          className={`st-arrow-badge st-arrow-badge--${badge.direction}`}
          style={{
            left: badge.x,
            top: badge.direction === "long" ? badge.y + 30 : badge.y - 44,
          }}
        >
          {badge.direction === "long" ? "▲ Buy" : "▼ Sell"}
        </div>
      ))}

      {/* ─── Dashboard card ─── */}
      <div className={`st-overlay ${sideClass}${showDashboard ? "" : " st-overlay--collapsed"}`}>
        {showDashboard ? (
          <>
            {/* Side accent bar (left edge) */}
            <div className="st-accent-bar" />

            {/* Header — symbol + price + collapse */}
            <div className="st-header">
              <div className="st-header-top">
                <span className="st-symbol">{symbol}</span>
                <button
                  className="st-toggle-btn"
                  onClick={onToggleDashboard}
                  title="Hide dashboard"
                  aria-label="Hide dashboard"
                >▲</button>
              </div>
              <span className="st-price">{currentPrice.toFixed(2)}</span>
            </div>

            {/* Brand banner */}
            <div className="st-brand-banner">
              <span className="st-brand-glow" />
              <span className="st-brand-text">SCALPING TREND</span>
              <span className="st-brand-glow" />
            </div>

            {/* Signal + Status pills */}
            <div className="st-pills">
              <div className={`st-signal-pill ${sideClass}`}>
                <span className="st-signal-dot" />
                <span className="st-signal-label">{signalLabel}</span>
              </div>
              <div className={`st-status-pill ${statusClass}`}>{statusText}</div>
            </div>

            {/* Levels grid */}
            <div className="st-rows">
              {rows.map((r, i) => (
                <div key={i} className={`st-row ${r.rowClass}${r.hit ? " st-row--hit" : ""}`}>
                  <span className="st-row-label">{r.label}</span>
                  <span className="st-row-value">
                    {fmt(r.value)}
                    {r.hit && r.value !== null && (
                      <span className="st-row-mark">{r.hitMark}</span>
                    )}
                  </span>
                </div>
              ))}
            </div>

            {/* Footer P&L */}
            <div className="st-pl-row">
              <div className="st-pl-cell">
                <span className="st-pl-label">Live P&amp;L</span>
                <span className={`st-pl-val ${livePL >= 0 ? "st-pl-pos" : "st-pl-neg"}`}>
                  {fmtPL(livePL)}
                </span>
              </div>
              <div className="st-pl-divider" />
              <div className="st-pl-cell">
                <span className="st-pl-label">Booked P&amp;L</span>
                <span className={`st-pl-val ${bookedPL >= 0 ? "st-pl-pos" : "st-pl-neg"}`}>
                  {fmtPL(bookedPL)}
                </span>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="st-collapsed-title">Scalping Trend</div>
            <button
              className="st-toggle-btn st-toggle-btn--collapsed"
              onClick={onToggleDashboard}
              title="Show dashboard"
              aria-label="Show dashboard"
            >▼</button>
          </>
        )}
      </div>
    </>
  );
};

export default ScalpingTrendOverlay;
